import { adaptivePoll } from '@/uptk/core/navigation/adaptivePoll';
/**
 * useRuntimeNavigation — Hook for consuming the runtime navigation manifest
 *
 * Fetches `GET /api/v1/navigation/runtime`, caches the result,
 * polls for version changes, and falls back to module registry
 * landing menus when the backend API is not available.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import {
  getRuntimeNavigationManifest,
  getRuntimeNavigationVersion,
  NavigationManagementApiError,
  dispatchRuntimeNavigationManifestInvalidated,
  runtimeNavigationManifestInvalidatedEventName,
  loadLocalRuntimeManifest,
  clearLocalRuntimeManifest,
  rebuildLocalRuntimeManifest,
} from '@/uptk/core/api/NavigationManagementApi';
import type {
  NavigationNode,
  NavigationPortalType,
  NavigationGuard,
} from '@/types/navigation_management';
import {
  getModuleLandingTopMenuItems,
  getModuleLandingTopMenuItemsExtended,
  loadModuleRegistryFromAuthContext,
} from '@/uptk/core/modules/registry';
import type { ModuleMenuItem, ModuleLandingMenuItem } from '@/uptk/core/modules/registry';
import {
  getAuthContextState,
  subscribeAuthContextState,
} from '@/uptk/core/api/auth_context_store';
import {
  filterNavigationNodesByModuleEntitlement,
  filterVisibleNavigationNodes,
  filterNavigationNodesByPermissions,
  resolveNavigationActiveTrail,
  sortNavigationNodes,
  substituteNavigationNodeTokens,
  type NavigationActiveTrail,
} from '@/utils/navigation_management';
import { isModuleOpen } from '@/uptk/core/modules/activeModules';
import { getPortalTypeFromPath } from '@/utils/route_builder';
import { parseRuntimeRoutePath } from '@/utils/runtime_route_paths';

// ─── Types ─────────────────────────────────────────────────────

export type RuntimeNavigationSource = 'backend' | 'local-manifest' | 'registry-fallback' | 'empty';

export type RuntimeNavigationState = {
  /** Whether the initial load is in progress */
  loading: boolean;
  /** Whether the backend navigation API is available */
  backendAvailable: boolean;
  /** Where the navigation nodes come from */
  source: RuntimeNavigationSource;
  /** The resolved navigation nodes (already filtered for visibility) */
  nodes: NavigationNode[];
  /** Active trail computed from current route */
  activeTrail: NavigationActiveTrail;
  /** Manifest version for cache busting */
  version: string | null;
  /** Error message if manifest fetch failed */
  error: string | null;
};

// ─── V2: Derive newItemPath from list routePath ────────────────
// Converts a plural list path (e.g. /portal/acme/2025/finance/budgets)
// to the singular new-item path (/portal/acme/2025/finance/budget/new).
// Only applies when the last segment looks like a plural entity slug.
const deriveNewItemPath = (routePath?: string | null): string | undefined => {
  if (!routePath) return undefined;
  const segments = routePath.replace(/^\/+/, '').split('/');
  if (segments.length < 3) return undefined;
  const lastSegment = segments[segments.length - 1];
  // Heuristic: if the last segment ends with 's' and isn't a year or ID,
  // treat it as a plural entity slug and derive the singular new path.
  // Examples: budgets→budget, invoices→invoice, teams→team
  // Skip: /finance (domain), /2025 (year), /parameter (domain)
  if (/^\d{4}$/.test(lastSegment)) return undefined;
  const domainSegments = new Set(['finance', 'parameter', 'pmo', 'sourcing', 'cbm', 'admin',
    'business', 'application', 'infrastructure', 'compliance', 'wealthtech']);
  if (domainSegments.has(lastSegment)) return undefined;
  // Try singularizing by removing trailing 's' (simple English heuristic)
  let singular = lastSegment;
  if (lastSegment.endsWith('ies')) {
    singular = lastSegment.slice(0, -3) + 'y'; // e.g. reclassifies → reclassify
  } else if (lastSegment.endsWith('es') && !lastSegment.endsWith('sses')) {
    singular = lastSegment.slice(0, -2); // e.g. invoices → invoic (not perfect, but rare)
  } else if (lastSegment.endsWith('s') && !lastSegment.endsWith('ss')) {
    singular = lastSegment.slice(0, -1); // e.g. budgets → budget
  } else {
    return undefined; // doesn't look plural
  }
  return `/${[...segments.slice(0, -1), singular].join('/')}/new`;
};

/**
 * Une entrée de pure navigation (page sans entité : « Horaires », « Feuille d'heures », « Calendrier des
 * présences » — `metadata.navigationOnly` posé par le manifeste) n'a pas de fiche « nouveau » : la dérivation
 * « liste au pluriel → /new » ne s'applique pas (07.10, Olivier : bouton « New Horaires » sans objet).
 */
export const isNavigationOnlyNode = (node: Pick<NavigationNode, 'metadata'>): boolean =>
  node.metadata?.navigationOnly === true;

/** Recursively enrich navigation nodes with newItemPath from their routePath */
const enrichNodesWithNewItemPath = (nodes: NavigationNode[]): NavigationNode[] =>
  nodes.map((node) => ({
    ...node,
    newItemPath:
      node.newItemPath ?? (isNavigationOnlyNode(node) ? undefined : deriveNewItemPath(node.routePath)),
    children: enrichNodesWithNewItemPath(node.children),
  }));

// ─── Conversion: Registry MenuItem → NavigationNode (single-group flyout) ────

const moduleIconMap: Record<string, string> = {
  finance: 'finance',
  project: 'project',
  dashboard: 'dashboard',
  business: 'business',
  infrastructure: 'infrastructure',
  compliance: 'compliance',
  parameter: 'parameter',
  iso2003: 'iso2003',
  iso22301: 'iso22301',
};

const convertExtendedItemToNode = (
  item: ModuleLandingMenuItem,
  portalType: NavigationPortalType
): NavigationNode => {
  // Build description from sub-menu labels
  const description = item.subMenuLabels.length > 0
    ? item.subMenuLabels.join(' · ')
    : undefined;

  // Convert sub-menus as children (so the flyout item can show sub-sections)
  const children: NavigationNode[] = item.subMenus.map((sm, idx) => ({
    id: `registry-${item.key}-sub-${sm.key}`,
    moduleCode: sm.moduleCode,
    portalType,
    kind: 'link' as const,
    key: sm.key,
    label: sm.label,
    routePath: sm.route || sm.portalRoute,
    newItemPath: deriveNewItemPath(sm.route || sm.portalRoute),
    order: idx,
    enabled: true,
    showInNavigation: true,
    guards: [],
    children: [],
  }));

  return {
    id: `registry-${item.key}`,
    moduleCode: item.moduleCode,
    portalType,
    kind: 'link',
    key: item.key,
    label: item.label,
    description,
    iconKey: moduleIconMap[item.key] ?? item.domain ?? 'default',
    routePath: item.route,
    newItemPath: deriveNewItemPath(item.route),
    order: item.order,
    enabled: true,
    showInNavigation: true,
    guards: [],
    children,
  };
};

const convertMenuItemToNode = (
  item: ModuleMenuItem,
  portalType: NavigationPortalType
): NavigationNode => ({
  id: `registry-${item.key}`,
  moduleCode: item.moduleCode,
  portalType,
  kind: item.route ? 'link' : 'group',
  key: item.key,
  label: item.label,
  routePath: item.route,
  newItemPath: deriveNewItemPath(item.route),
  order: item.order,
  enabled: true,
  showInNavigation: true,
  guards: [],
  children: [],
});

// ─── Conversion: AuthContextMenuItem → NavigationNode ────────────

type AuthContextMenuItemLike = {
  id: string;
  route: string;
  label: string;
  requiresModule?: string;
};

const convertAuthMenuToNode = (
  item: AuthContextMenuItemLike,
  portalType: NavigationPortalType,
  index: number
): NavigationNode => {
  const guards: NavigationGuard[] = [];
  if (item.requiresModule) {
    guards.push({ type: 'module-entitlement', value: item.requiresModule });
  }
  return {
    id: `authmenu-${item.id || index}`,
    moduleCode: item.requiresModule ?? 'core',
    portalType,
    kind: 'link',
    key: item.label || `authmenu-${index}`,
    label: item.label,
    routePath: item.route,
    newItemPath: deriveNewItemPath(item.route),
    order: index,
    enabled: true,
    showInNavigation: true,
    guards,
    children: [],
  };
};

// ─── Hook ──────────────────────────────────────────────────────

const versionPollIntervalMs = 60000;

// Module-level cache: remember if the backend returned 404 for the runtime
// navigation endpoint so we don't keep hitting it every render. Reset on reload.
let backendNavigationUnavailable = false;

// The registry/auth-context fallback fabricates a navigation menu from client-side
// module metadata when the backend manifest is unavailable or empty. That is a
// development convenience only — in QA/PROD it would mask a broken or unpublished
// backend by rendering a plausible-but-wrong menu, so we gate it off there.
// Default: enabled in dev builds, disabled otherwise; override via
// VITE_NAV_REGISTRY_FALLBACK ('true' | 'false').
const isDevNavigationFallbackEnabled = ((): boolean => {
  const explicit = import.meta.env.VITE_NAV_REGISTRY_FALLBACK;
  if (explicit === 'true') return true;
  if (explicit === 'false') return false;
  return import.meta.env.DEV === true;
})();

export function resetNavigationAvailability(): void {
  backendNavigationUnavailable = false;
}

export function useRuntimeNavigation(): RuntimeNavigationState {
  const location = useLocation();
  const currentPathname = location.pathname;
  const detectedPortalType = getPortalTypeFromPath(currentPathname);
  // getPortalTypeFromPath returns the wider PortalType (e.g. 'marketplace' or
  // arbitrary strings); navigation only supports the three NavigationPortalType
  // values, so map anything else to the 'portal' default.
  const portalType: NavigationPortalType =
    detectedPortalType === 'adminportal'
      ? 'adminportal'
      : detectedPortalType === 'make'
        ? 'make'
        : 'portal';

  const [loading, setLoading] = useState(true);
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [source, setSource] = useState<RuntimeNavigationSource>('empty');
  // Raw unfiltered nodes from the backend/fallback (sorted but not yet filtered
  // for entitlements or permissions). Visibility filtering is applied reactively
  // in `filteredNodes` below so that auth-context/entitlement updates re-filter
  // existing nodes without triggering a manifest re-fetch.
  const [rawNodes, setRawNodes] = useState<NavigationNode[]>([]);
  const [version, setVersion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [authContext, setAuthContext] = useState(() => getAuthContextState());

  const isActiveRef = useRef(true);
  // 22.09 (DEV, page event/events) : nombre de nouvelles tentatives déjà faites sur
  // une erreur passagère du manifeste ; remis à zéro à la première réponse valide.
  const retryCountRef = useRef(0);
  const loadManifestRef = useRef<() => Promise<void>>(async () => {});
  const workspaceId = authContext?.workspaceId;
  const tenantId = authContext?.tenantId;

  // Module entitlement state: disabled modules must disappear from the dynamic navigation. UPT-2069 (L1) : la
  // décision vient de l'auth-context seul (isModuleOpen : code, alias ou domaine, ACTIVE ou TRIAL) ; avant son arrivée
  // le snapshot localStorage n'est qu'un cache, et son absence masque les nœuds de module au lieu de tout montrer.
  const applyVisibilityFilters = useCallback(
    (input: NavigationNode[]): NavigationNode[] =>
      filterNavigationNodesByPermissions(
        filterNavigationNodesByModuleEntitlement(
          filterVisibleNavigationNodes(input),
          (moduleCode) => isModuleOpen(moduleCode, { context: authContext ?? null, tenantId: tenantId ?? undefined })
        ),
        (permission) => {
          // Permission verdict from the auth context ("ENTITY.PERMISSION"
          // strings, e.g. "AI_SCAN.VIEW"). Unknown state keeps nodes visible.
          if (!authContext) {
            return true;
          }
          return (authContext.permissions ?? []).includes(permission);
        }
      ),
    [tenantId, authContext]
  );

  // Reactive visibility filter + token enrichment. Re-runs when raw nodes or
  // the filter function changes — no manifest re-fetch, no loading flash.
  const filteredNodes = useMemo(
    () => enrichNodesWithNewItemPath(applyVisibilityFilters(rawNodes)),
    [rawNodes, applyVisibilityFilters]
  );

  // Auth context ref — keeps loadManifest stable when only permissions/menus
  // change (no workspace/tenant change) so we don't re-fetch the manifest on
  // every auth context update. Only workspaceId/tenantId should trigger a
  // re-fetch (different manifest query params).
  const authContextRef = useRef(authContext);
  authContextRef.current = authContext;

  // Subscribe to auth context changes
  useEffect(() => {
    return subscribeAuthContextState((nextContext) => {
      setAuthContext(nextContext);
    });
  }, []);

  // Load manifest
  const loadManifest = useCallback(async () => {
    if (!isActiveRef.current) return;
    let retryScheduled = false;

    // Helper: build fallback nodes from registry + auth context menus.
    // Uses authContextRef so this closure does not have authContext as a dep —
    // the fallback path is DEV-only and its staleness is not user-visible.
    const buildFallbackNodes = (): { nodes: NavigationNode[]; source: RuntimeNavigationSource } => {
      // In QA/PROD the client-side fabrication is disabled so a backend problem
      // surfaces as an empty menu rather than being masked by invented nodes.
      if (!isDevNavigationFallbackEnabled) {
        if (import.meta.env.DEV === false) {
          // eslint-disable-next-line no-console -- diagnostic aid for non-dev builds
          console.warn(
            '[navigation] runtime manifest unavailable or empty; registry fallback is disabled, showing no menu.'
          );
        }
        return { nodes: [], source: 'empty' as const };
      }

      const ctx = authContextRef.current;
      // 1. Try registry landing menus (extended with sub-menu info)
      if (ctx) {
        loadModuleRegistryFromAuthContext(ctx);
      }
      const registryContext = portalType === 'make' ? 'adminportal' : portalType;
      const extendedItems = getModuleLandingTopMenuItemsExtended(registryContext);
      if (extendedItems.length > 0) {
        // Wrap all landing menu items in a single "Modules" group node
        // so the header renders ONE flyout button instead of flat tabs
        const childNodes = extendedItems.map((item) =>
          convertExtendedItemToNode(item, portalType)
        );
        const modulesGroup: NavigationNode = {
          id: 'modules-flyout',
          moduleCode: 'core',
          portalType,
          kind: 'group',
          key: 'menu.modules',
          label: 'Modules',
          iconKey: 'default',
          order: 0,
          enabled: true,
          showInNavigation: true,
          guards: [],
          children: childNodes,
        };
        return {
          nodes: [modulesGroup],
          source: 'registry-fallback' as const,
        };
      }

      // 2. Try basic registry landing menus (no sub-menu info) (no sub-menu info)
      const registryItems = getModuleLandingTopMenuItems(registryContext);
      if (registryItems.length > 0) {
        const childNodes = registryItems.map((item) =>
          convertMenuItemToNode(item, portalType)
        );
        const modulesGroup: NavigationNode = {
          id: 'modules-flyout',
          moduleCode: 'core',
          portalType,
          kind: 'group',
          key: 'menu.modules',
          label: 'Modules',
          iconKey: 'default',
          order: 0,
          enabled: true,
          showInNavigation: true,
          guards: [],
          children: childNodes,
        };
        return {
          nodes: [modulesGroup],
          source: 'registry-fallback' as const,
        };
      }

      // 3. Try auth context menus (users for portal, admins for adminportal)
      const authMenus = ctx?.menus;
      const menuKey = portalType === 'adminportal' ? 'admins' : 'users';
      const contextMenuItems = Array.isArray((authMenus as Record<string, unknown>)?.[menuKey])
        ? (authMenus as Record<string, AuthContextMenuItemLike[]>)[menuKey]
        : [];
      const validMenuItems = contextMenuItems.filter(
        (item) => item && typeof item.route === 'string' && item.route.trim().length > 0
      );
      if (validMenuItems.length > 0) {
        const childNodes = validMenuItems.map((item, idx) =>
          convertAuthMenuToNode(item, portalType, idx)
        );
        const modulesGroup: NavigationNode = {
          id: 'modules-flyout',
          moduleCode: 'core',
          portalType,
          kind: 'group',
          key: 'menu.modules',
          label: 'Modules',
          iconKey: 'default',
          order: 0,
          enabled: true,
          showInNavigation: true,
          guards: [],
          children: childNodes,
        };
        return {
          nodes: [modulesGroup],
          source: 'registry-fallback' as const,
        };
      }

      return { nodes: [], source: 'empty' as const };
    };

    // If the backend already told us the navigation endpoint doesn't exist (404),
    // skip the API call and go straight to the fallback. This avoids a redundant
    // 404 request in the Network tab on every render/revalidation cycle.
    if (backendNavigationUnavailable) {
      const localManifest = loadLocalRuntimeManifest(portalType, workspaceId ?? '*');
      if (localManifest && localManifest.nodes.length > 0) {
        setRawNodes(sortNavigationNodes(localManifest.nodes));
        setVersion(localManifest.version);
        setBackendAvailable(false);
        setSource('local-manifest');
        setError(null);
      } else {
        const fallback = buildFallbackNodes();
        setRawNodes(fallback.nodes);
        setVersion(null);
        setBackendAvailable(false);
        setSource(fallback.source);
        setError(null);
      }
      setLoading(false);
      return;
    }

    try {
      const result = await getRuntimeNavigationManifest({
        portalType,
        workspaceId: workspaceId ?? undefined,
        tenantId: tenantId ?? undefined,
      });

      if (!isActiveRef.current) return;
      retryCountRef.current = 0;

      if (result) {
        const sortedNodes = sortNavigationNodes(result.nodes);

        // Backend returned a manifest but with empty nodes. Mirror the 404/error
        // paths: consult the local manifest first, then the (dev-gated) fallback,
        // so a 200-with-no-nodes degrades consistently instead of jumping straight
        // to fabricated content.
        if (sortedNodes.length === 0) {
          const localManifest = loadLocalRuntimeManifest(portalType, workspaceId ?? '*');
          if (localManifest && localManifest.nodes.length > 0) {
            setRawNodes(sortNavigationNodes(localManifest.nodes));
            setVersion(localManifest.version);
            setBackendAvailable(false);
            setSource('local-manifest');
            setError(null);
          } else {
            const fallback = buildFallbackNodes();
            setRawNodes(fallback.nodes);
            setVersion(null);
            setBackendAvailable(false);
            setSource(fallback.source);
            setError(null);
          }
        } else {
          setRawNodes(sortedNodes);
          setVersion(result.version);
          setBackendAvailable(true);
          setSource('backend');
          setError(null);
        }
      } else {
        // Backend returned 404 — try local manifest first, then fallbacks
        // Remember that the endpoint is unavailable to avoid repeated 404s
        backendNavigationUnavailable = true;
        const localManifest = loadLocalRuntimeManifest(portalType, workspaceId ?? '*');
        if (localManifest && localManifest.nodes.length > 0) {
          setRawNodes(sortNavigationNodes(localManifest.nodes));
          setVersion(localManifest.version);
          setBackendAvailable(false);
          setSource('local-manifest');
          setError(null);
        } else {
          const fallback = buildFallbackNodes();
          setRawNodes(fallback.nodes);
          setVersion(null);
          setBackendAvailable(false);
          setSource(fallback.source);
          setError(null);
        }
      }
    } catch (err) {
      if (!isActiveRef.current) return;
      // 22.09 (DEV, page event/events) : une erreur passagère (réseau, 5xx pendant un
      // redéploiement) ne doit pas rabattre le header sur la barre plate pour toute la
      // vie de la page — on réessaie deux fois (4 s puis 8 s), le header reste en
      // attente pendant ce temps, avant de se replier comme avant.
      const status = err instanceof NavigationManagementApiError ? err.status : null;
      const transient = status == null || status >= 500;
      if (transient && retryCountRef.current < 2) {
        retryCountRef.current += 1;
        retryScheduled = true;
        window.setTimeout(() => {
          if (isActiveRef.current) void loadManifestRef.current();
        }, 4000 * retryCountRef.current);
        return;
      }
      // Fall back to local manifest first, then registry on any error
      // Remember that the endpoint is unavailable to avoid repeated 404s/network errors
      backendNavigationUnavailable = true;
      const localManifest = loadLocalRuntimeManifest(portalType, workspaceId ?? '*');
      if (localManifest && localManifest.nodes.length > 0) {
        setRawNodes(sortNavigationNodes(localManifest.nodes));
        setVersion(localManifest.version);
        setBackendAvailable(false);
        setSource('local-manifest');
        setError(null);
      } else {
        const fallback = buildFallbackNodes();
        setRawNodes(fallback.nodes);
        setVersion(null);
        setBackendAvailable(false);
        setSource(fallback.source);
        setError(
          err instanceof NavigationManagementApiError
            ? err.message
            : 'Failed to load navigation manifest.'
        );
      }
    } finally {
      if (isActiveRef.current && !retryScheduled) {
        setLoading(false);
      }
    }
  }, [portalType, tenantId, workspaceId]);
  loadManifestRef.current = loadManifest;

  // Initial load + invalidation listener
  useEffect(() => {
    isActiveRef.current = true;
    setLoading(true);
    // Reset the "backend unavailable" flag on every mount so a recovered
    // backend gets retried. Without this, a single 404/error permanently
    // disables backend navigation for the entire browser session, leaving
    // the user stuck on a stale localStorage cache that never updates.
    resetNavigationAvailability();
    // Evict any stale local manifest cache from a previous session so the
    // backend is always the source of truth on fresh page loads. The local
    // cache is only a transient fallback during an active backend outage.
    clearLocalRuntimeManifest();
    void loadManifest();

    const handleInvalidation = () => {
      void loadManifest();
    };

    window.addEventListener(
      runtimeNavigationManifestInvalidatedEventName,
      handleInvalidation
    );

    return () => {
      isActiveRef.current = false;
      window.removeEventListener(
        runtimeNavigationManifestInvalidatedEventName,
        handleInvalidation
      );
    };
  }, [loadManifest]);

  // Stable per SaaS context: manifest updates do not restart the timer or probe baseline.
  useEffect(() => {
    if (!backendAvailable || backendNavigationUnavailable) return;
    let previous: string | undefined;
    return adaptivePoll(async () => {
      const result = await getRuntimeNavigationVersion({ portalType, workspaceId: workspaceId ?? undefined, tenantId: tenantId ?? undefined });
      if (!result) return false;
      const token = JSON.stringify([result.version, result.checksum]);
      if (token === previous) return false;
      await loadManifestRef.current();
      previous = token;
      return true;
    }, { baseMs: versionPollIntervalMs });
  }, [backendAvailable, portalType, tenantId, workspaceId]);

  // Resolve `:workspaceId` / `:year` tokens in the authored route paths using the
  // current portal context. Prefer the values from the URL the user is actually
  // viewing (so a nav link inherits the current workspace + year), falling back
  // to the auth-context workspace when the current route carries no scope.
  const parsedCurrentPath = useMemo(
    () => parseRuntimeRoutePath(currentPathname),
    [currentPathname]
  );
  // The selected fiscal year lives in redux (seeded from localStorage / current year).
  const selectedYear = useSelector((state: { year?: string }) => state.year);
  const activeWorkspaceId = parsedCurrentPath.workspaceId ?? workspaceId ?? null;
  // Mirror the workspace fallback chain: URL first, then app selection, then the
  // current year. Leaving this null leaves a literal `:year` in nav hrefs on pages
  // without a year segment (e.g. the portal landing), which the RR7 splat rejects
  // as "outside the managed route grammar".
  const activeYear =
    parsedCurrentPath.year ?? selectedYear ?? String(new Date().getFullYear());

  const resolvedNodes = useMemo(
    () =>
      substituteNavigationNodeTokens(filteredNodes, {
        workspaceId: activeWorkspaceId,
        year: activeYear,
      }),
    [filteredNodes, activeWorkspaceId, activeYear]
  );

  // Resolve active trail against the substituted paths so highlighting matches
  // the concrete URLs in the address bar.
  const activeTrail = useMemo(
    () => resolveNavigationActiveTrail(resolvedNodes, currentPathname),
    [resolvedNodes, currentPathname]
  );

  return {
    loading,
    backendAvailable,
    source,
    nodes: resolvedNodes,
    activeTrail,
    version,
    error,
  };
}

export default useRuntimeNavigation;
